import { useMemo } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useCallCardFields } from '../../contexts/CallCardFieldsContext';
import {
  isInternalCall,
  resolveCallOutcome,
  resolveCallService,
  resolveCallSummary,
  resolveCallTypeLabel,
  resolveChecklistFormLabel,
  resolveComplaints,
  resolveObjectionLabels,
  resolveOperatorTotal
} from '../../utils/callNodeFields';
import { formatCallDirection } from '../../utils/labels';
import {
  formatEvidence,
  formatOperatorTotal,
  formatTranscriptLines,
  orderChecklistEntries,
  resolveChecklistScoreStyle,
  resolveRecordingUrl
} from './callDetailFormat';
import { CALL_CARD_FIELD_SLOTS } from './callCardFields';

const { SERVICE, OUTCOME } = CALL_CARD_FIELD_SLOTS;

// `field` — слот настраиваемого поля: по нему e2e находит блок, чьё название переименовал тенант.
export const InfoBlock = ({ label, value, field }) => (
  <div className="min-w-0 rounded-md border bg-card p-3" data-call-card-field={field}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm font-medium text-foreground mt-1 [overflow-wrap:anywhere]">{value ?? '—'}</div>
  </div>
);

/**
 * Единственный вид карточки звонка: журнал «Детали звонков», примеры звонков из графиков,
 * раскрывашки на «Клиентах» и «Жалобах» рендерят именно её.
 *
 * Обёртки нет намеренно — панель одинаково встаёт и в `DialogContent`, и в ячейку таблицы,
 * поэтому скролл и максимальную высоту задаёт вызывающая сторона.
 *
 * Ждёт целиком `CallDetailsItemDto` (`GET /api/analytics/calls` и те эндпоинты, что собирают
 * звонки тем же `BuildCallDetailsSelectSql`), а не урезанную проекцию.
 */
const CallDetailPanel = ({ call }) => {
  const { features } = useFeatures();
  // Гейт живёт внутри панели, а не в пропе: иначе очередная точка встраивания забыла бы
  // его прокинуть, и тумблер молча перестал бы действовать в одном из разделов.
  const transcriptsVisible = features.transcriptsVisible;
  // Поля «Услуга» и «Итог звонка»: название и видимость задаёт тенант, значение — сервер.
  const cardFields = useCallCardFields();
  const outcomeVisible = cardFields.isVisible(OUTCOME);

  // При выключенном тумблере сервер отдаёт transcript = null, так что здесь получится пустой
  // массив — но блок мы всё равно прячем целиком, а не показываем «Нет транскрипта».
  const transcriptLines = useMemo(
    () => formatTranscriptLines(call?.transcript),
    [call?.transcript]
  );

  // Подтверждение исхода: в legacy это цитата воронки, в режиме нод — обоснование triage.
  const bookingOutcomeEvidence = useMemo(
    () => formatEvidence(resolveCallOutcome(call)?.reason),
    [call]
  );

  if (!call) {
    return null;
  }

  const recordingUrl = resolveRecordingUrl(call);
  // Вывод анализа, а не текст диалога: тумблер расшифровок его не гасит, как и цитаты нод.
  const summary = resolveCallSummary(call);
  const objectionLabels = resolveObjectionLabels(call);
  const checklistEntries = orderChecklistEntries(call.checklist);
  const isInternal = isInternalCall(call);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 max-sm:gap-2 md:grid-cols-4">
        <InfoBlock label="Телефон клиента" value={call.clientPhone ?? '—'} />
        <InfoBlock label="Направление" value={formatCallDirection(call.callType)} />
        <InfoBlock label="Тема" value={resolveCallTypeLabel(call) ?? '—'} />
        {cardFields.isVisible(SERVICE) && (
          <InfoBlock field={SERVICE} label={cardFields.title(SERVICE)} value={resolveCallService(call) ?? '—'} />
        )}
        {outcomeVisible && (
          <InfoBlock field={OUTCOME} label={cardFields.title(OUTCOME)} value={resolveCallOutcome(call)?.label ?? '—'} />
        )}
        <InfoBlock label="Форма чек-листа" value={resolveChecklistFormLabel(call) ?? '—'} />
        <InfoBlock label="Итог чек-листа" value={formatOperatorTotal(resolveOperatorTotal(call))} />
      </div>

      {summary && (
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-foreground">Краткое содержание</h4>
          <div className="rounded-md bg-muted p-3 text-sm leading-relaxed text-foreground [overflow-wrap:anywhere]">
            {summary}
          </div>
        </div>
      )}

      {/* Подтверждение — обоснование того же выхода, что и итог: скрыт итог — скрыто и оно. */}
      {outcomeVisible && bookingOutcomeEvidence && (
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-foreground">Подтверждение исхода</h4>
          <div className="rounded-md bg-muted p-3 text-sm italic leading-relaxed text-muted-foreground">
            «{bookingOutcomeEvidence}»
          </div>
        </div>
      )}

      {!isInternal && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-foreground">Возражения</h4>
          {objectionLabels.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {objectionLabels.map((label) => (
                <Badge key={label} variant="outline" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                  {label}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Возражений не зафиксировано.</p>
          )}
        </div>
      )}

      {!isInternal && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-foreground">Чек-лист</h4>
          <div className="rounded-md bg-muted p-3 text-sm leading-relaxed space-y-3">
            {checklistEntries.length === 0 && (
              <p className="text-muted-foreground">Нет данных чек-листа.</p>
            )}
            {checklistEntries.map(([key, item]) => {
              const { symbol, className } = resolveChecklistScoreStyle(item?.score);
              const shouldShowEvidence = item?.evidence && Number(item?.score) < 1;
              return (
                <div key={key} className="space-y-1 border-b border-border/50 pb-2 last:border-b-0 last:pb-0">
                  <div className="flex items-center justify-between gap-2">
                    <span>{item?.label ?? key}</span>
                    <span className={`whitespace-nowrap font-medium ${className}`}>{symbol}</span>
                  </div>
                  {item?.reason && (
                    <div className="text-xs text-muted-foreground">Причина: {item.reason}</div>
                  )}
                  {shouldShowEvidence && (
                    <div className="text-xs italic text-muted-foreground">«{formatEvidence(item.evidence)}»</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isInternal && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-foreground">Жалобы</h4>
          <div className="rounded-md bg-muted p-3 text-sm leading-relaxed">
            {resolveComplaints(call)}
          </div>
        </div>
      )}

      {transcriptsVisible && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-foreground">Расшифровка звонка</h4>
          <div className="rounded-md bg-muted p-3 text-sm leading-relaxed space-y-2">
            {transcriptLines.length
              ? transcriptLines.map((line, index) => (
                  <div key={index} className="whitespace-pre-line break-words">
                    {line}
                  </div>
                ))
              : 'Нет транскрипта'}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground">Прослушать запись</h4>
          {recordingUrl && (
            <Button asChild size="sm" variant="outline" data-ev="calls.detail.recording.download">
              <a href={recordingUrl} download data-ev="calls.detail.recording.download">
                Скачать
              </a>
            </Button>
          )}
        </div>
        {recordingUrl ? (
          <audio className="w-full" src={recordingUrl} controls data-ev="calls.detail.recording" />
        ) : (
          <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
            Запись недоступна
          </div>
        )}
      </div>
    </div>
  );
};

export default CallDetailPanel;
